// ── 위젯 메뉴가 없는 손님 테넌트 직원 — 카드 요약만 받기 (2026-10-24, 테넌트별 위젯 2단계) ─────────────────
// 서버 /my/situation-widgets 가 위젯마다 summary_only 를 준다(그 위젯 메뉴 열쇠가 없음). 그런 위젯은
//   ① 원래 데이터 주소(직원 목록·한 달 출퇴근 …)를 부르지 않고 /api/my/widget-summaries 에서 카드에 보일 숫자만 받는다
//   ② 숫자를 누르면 명단 대신 회색 안내 한 줄 — 사용자 결정(나) "자세한 명단은 담당자 화면에서 볼 수 있어요".
// 메뉴를 가진 직원·운영자는 summary_only 가 없어 이 훅이 아무것도 바꾸지 않는다.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiClient } from '../../../api/apiClient';

// 계약 표 2종은 달을 넘길 때마다 카드가 직접 요약 길에서 받는다(useContractMonth summaryCode) — 한꺼번에 받기에서 뺀다
const SELF_FETCH = new Set(['contracts_by_marketer', 'contracts_by_consultant']);
const NOTICE = '자세한 명단은 담당자 화면에서 볼 수 있어요';
// 요약 위젯 → 그 카드 숫자를 누르면 불리던 명단 함수(useSituationBoard handlers)
const HANDLERS_BY_WIDGET = {
  kpi_employees: ['onClickEmployees'],
  kpi_external_users: ['onClickExternal'],
  kpi_sales_roster: ['onClickSales'],
  attendance_today: ['onClickCheckedIn', 'onClickCheckedOut', 'onClickNotOut'],
  month_call_contract: ['onClickMonthCalls', 'onClickMonthContracts', 'onClickConversionRate', 'onClickPending'],
};

export function useWidgetSummaries({ widgets, visibleCodes, enabled, showToast }) {
  const summaryCodes = useMemo(() => {
    const visible = new Set(visibleCodes);
    return (widgets || []).filter((w) => w.summary_only === true && visible.has(w.code)).map((w) => w.code);
  }, [widgets, visibleCodes]);
  const summarySet = useMemo(() => new Set(summaryCodes), [summaryCodes]);
  const key = summaryCodes.filter((c) => !SELF_FETCH.has(c)).join(',');
  const [data, setData] = useState({});

  const reload = useCallback(async () => {
    if (!enabled || !key) { setData({}); return; }
    try {
      const res = await apiClient.get(`/api/my/widget-summaries?codes=${encodeURIComponent(key)}`);
      setData(res.ok && res.data?.data && typeof res.data.data === 'object' ? res.data.data : {});
    } catch (_) {
      setData({});
    }
  }, [enabled, key]);

  useEffect(() => { reload(); }, [reload]);

  /** 화면 통계(useSituationStats)에 요약 숫자를 덮어 쓴다 — 요약 위젯의 칸만 */
  const applyStats = useCallback((stats) => {
    let next = stats;
    for (const code of summaryCodes) {
      const p = data[code];
      if (p && typeof p === 'object' && !p.error) next = { ...next, ...p };
    }
    return next;
  }, [data, summaryCodes]);

  /** 요약 위젯의 숫자 누름 → 명단 대신 안내 한 줄 */
  const wrapHandlers = useCallback((handlers) => {
    if (!summaryCodes.length) return handlers;
    const next = { ...handlers };
    const notice = () => showToast?.(NOTICE, 'notice');
    for (const code of summaryCodes) {
      for (const name of HANDLERS_BY_WIDGET[code] || []) next[name] = notice;
    }
    return next;
  }, [summaryCodes, showToast]);

  return { summarySet, applyStats, wrapHandlers, reload };
}
